import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useNavigate } from 'react-router-dom';
import { get } from '../../utils/api';
import { getKotilingaCategorySlugsCsv } from '../../utils/kotilingaScope';

function formatAmount(n) {
  const v = Number(n);
  if (!isFinite(v)) return '--';
  return v.toLocaleString(undefined, { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 });
}

// Donations by Customer Dialog
export function DonationsByCustomerDialog({ open, onClose, product, buildRangeParams, useParentCategorySlugs }) {
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [expandedCustomers, setExpandedCustomers] = React.useState({});
  const [customerTransactions, setCustomerTransactions] = React.useState({});
  const [loadingTransactions, setLoadingTransactions] = React.useState({});

  // Load donations when dialog opens or product changes
  React.useEffect(() => {
    if (!open || !product) {
      setRows([]);
      setExpandedCustomers({});
      setCustomerTransactions({});
      setLoadingTransactions({});
      return;
    }

    const loadDonations = async () => {
      try {
        const pid = product.product_id;
        const vid = product.variation?.variation_id;
        
        const range = buildRangeParams();
        const scopeSlugs = getKotilingaCategorySlugsCsv();
        const categoryParam = useParentCategorySlugs
          ? `parent_category_slugs=${encodeURIComponent(useParentCategorySlugs)}`
          : `exclude_parent_category_slugs=${encodeURIComponent(scopeSlugs)}`;
        
        if (product.variation && vid !== null && vid !== undefined) {
          // Use the backend endpoint for variation-specific donations
          const res = await get(`/ldb/stats/product-variations/${pid}/${vid}/customers?${categoryParam}${range}`);
          const data = Array.isArray(res?.data) ? res.data : [];
          setRows(data);
        } else {
          // Product-level: fetch all orders for this product and aggregate by customer
          const rangeParams = buildRangeParams ? buildRangeParams() : '';
          const categoryQuery = useParentCategorySlugs
            ? `&parent_category_slugs=${encodeURIComponent(useParentCategorySlugs)}`
            : `&exclude_parent_category_slugs=${encodeURIComponent(scopeSlugs)}`;
          
          // Fetch all completed orders for this product
          let allOrders = [];
          let page = 1;
          const perPage = 100;
          
          while (true) {
            const res = await get(`/ldb/orders/search?product_id=${encodeURIComponent(pid)}&status=completed&page=${page}&per_page=${perPage}${categoryQuery}${rangeParams}`);
            const batch = Array.isArray(res?.data) ? res.data : [];
            
            // Filter to only orders that have this product
            const filtered = batch.filter(o => {
              const items = o.items || o.line_items || [];
              return items.some(item => String(item.product_id || item.productId) === String(pid));
            });
            
            allOrders = allOrders.concat(filtered);
            
            if (batch.length < perPage || !res?.meta || page >= Math.ceil((res.meta.total || 0) / perPage)) break;
            page++;
          }
          
          // Aggregate by customer
          const customerMap = new Map();
          allOrders.forEach(order => {
            const email = String(order.billing_email || '').trim().toLowerCase();
            const customerId = Number(order.customer_id || 0);
            const customerKey = customerId ? `cust#${customerId}` : (email || `order#${order.id}`);
            
            const items = order.items || order.line_items || [];
            const productItems = items.filter(item => String(item.product_id || item.productId) === String(pid));
            const productAmount = productItems.reduce((sum, item) => {
              return sum + Number(item.total_num || item.subtotal_num || item.price_num * item.quantity || 0);
            }, 0);
            const variationCount = (() => {
              if (!productItems.length) return 0;
              const ids = new Set(
                productItems.map(item => String(item.variation_id ?? 0))
              );
              return Math.max(1, ids.size);
            })();
            
            const existing = customerMap.get(customerKey) || {
              customer_id: customerId || null,
              name: `${order.billing_first_name || ''} ${order.billing_last_name || ''}`.trim() || (order.billing_email || 'Unknown'),
              email: order.billing_email || '',
              billing_phone: order.billing_phone || '',
              customer_key: customerKey,
              total_amount: 0,
              order_count: 0
            };
            
            existing.total_amount += productAmount;
            existing.order_count += variationCount;
            
            customerMap.set(customerKey, existing);
          });
          
          const aggregatedData = Array.from(customerMap.values())
            .sort((a, b) => b.total_amount - a.total_amount)
            .map((item, idx) => ({ ...item, serial: idx + 1 }));
          
          setRows(aggregatedData);
        }
      } catch (e) {
        console.error('[DonationsByCustomerDialog] Failed to load donations:', e);
        setRows([]);
      }
    };

    loadDonations();
  }, [open, product, buildRangeParams]);

  const toggleCustomerTransactions = async (customerKey, customer) => {
    const isExpanded = !!expandedCustomers[customerKey];
    if (isExpanded) {
      setExpandedCustomers(prev => {
        const next = { ...prev };
        delete next[customerKey];
        return next;
      });
      return;
    }

    setExpandedCustomers(prev => ({ ...prev, [customerKey]: true }));
    
    if (customerTransactions[customerKey]) {
      return;
    }

    setLoadingTransactions(prev => ({ ...prev, [customerKey]: true }));
    
    try {
      const pid = product?.product_id;
      const vid = product?.variation?.variation_id;
      
      if (!pid) {
        setLoadingTransactions(prev => {
          const next = { ...prev };
          delete next[customerKey];
          return next;
        });
        return;
      }

      const range = buildRangeParams();
      // Extract and decode date parameters from buildRangeParams() string
      // buildRangeParams() returns URL-encoded values like "&start=2025-01-05T20%3A15%3A31.046Z&end=..."
      const rangeParams = {};
      if (range) {
        const startMatch = range.match(/start=([^&]+)/);
        const endMatch = range.match(/end=([^&]+)/);
        if (startMatch?.[1]) rangeParams.start = decodeURIComponent(startMatch[1]);
        if (endMatch?.[1]) rangeParams.end = decodeURIComponent(endMatch[1]);
      }
      
      // Use customer_key from customer object for matching
      const targetCustomerKey = customer?.customer_key || customerKey;
      console.log('[DonationsByCustomerDialog] Loading transactions for customer:', {
        customerKey,
        targetCustomerKey,
        customer: customer?.name,
        product_id: pid,
        variation_id: vid
      });
      
      let allOrders = [];
      let page = 1;
      const perPage = 100;
      
      while (true) {
        const params = {
          product_id: pid,
          status: 'completed',
          page,
          per_page: perPage,
          ...(useParentCategorySlugs ? { parent_category_slugs: useParentCategorySlugs } : { exclude_parent_category_slugs: getKotilingaCategorySlugsCsv() }),
          ...rangeParams
        };
        
        if (customer?.customer_id) {
          params.customer_id = customer.customer_id;
        }
        
        // Only add variation_id if we have one
        if (vid !== null && vid !== undefined && Number(vid) !== 0) {
          params.variation_ids = String(vid);
        }
        
        const res = await get('/ldb/orders/search', params);
        const batch = Array.isArray(res?.data) ? res.data : [];
        
        // Filter by customer key - use customer_key from customer object for matching
        const filtered = batch.filter(o => {
          // Construct customer key from order (same logic as in loadDonations)
          const email = String(o.billing_email || '').trim().toLowerCase();
          const customerId = Number(o.customer_id || 0);
          const orderCustomerKey = customerId ? `cust#${customerId}` : (email || `order#${o.id}`);
          
          // Match customer key
          const matches = orderCustomerKey === targetCustomerKey;
          if (matches && page === 1) {
            console.log(`[DonationsByCustomerDialog] ✅ Matched order ${o.id}:`, {
              orderCustomerKey,
              targetCustomerKey,
              billing_email: o.billing_email,
              customer_id: o.customer_id
            });
          }
          return matches;
        });
        
        console.log(`[DonationsByCustomerDialog] Page ${page}: ${batch.length} orders fetched, ${filtered.length} matched customer key "${targetCustomerKey}"`);
        
        allOrders = allOrders.concat(filtered);
        
        if (batch.length < perPage || !res?.meta || page >= Math.ceil((res.meta.total || 0) / perPage)) break;
        page += 1;
      }
      
      console.log(`[DonationsByCustomerDialog] Total orders matched: ${allOrders.length}`);
      
      // Enrich with full order details and filter by product/variation
      console.log(`[DonationsByCustomerDialog] Enriching ${allOrders.length} orders...`);
      const enriched = await Promise.all(allOrders.map(async (o) => {
        try {
          const det = await get(`/ldb/orders/${o.id}`);
          const orderData = det?.order || det || {};
          const items = Array.isArray(det?.items) ? det.items : [];
          
          console.log(`[DonationsByCustomerDialog] Order ${o.id}: ${items.length} items`, {
            product_id: pid,
            variation_id: vid,
            item_product_ids: items.map(i => i.product_id),
            item_variation_ids: items.map(i => i.variation_id)
          });
          
          // Filter items by product (and variation if specified)
          const relevantItems = vid !== null && vid !== undefined
            ? items.filter(item => 
                item.product_id === pid && 
                (item.variation_id === vid || (vid === 0 && !item.variation_id))
              )
            : items.filter(item => item.product_id === pid);
          
          console.log(`[DonationsByCustomerDialog] Order ${o.id}: ${relevantItems.length} relevant items after filtering`);
          
          // Only include orders that have the product/variation
          if (relevantItems.length === 0) {
            console.warn(`[DonationsByCustomerDialog] Order ${o.id} filtered out - no relevant items`);
            return null;
          }
          
          const amount = relevantItems.reduce((sum, item) => sum + Number(item.total_num || 0), 0);
          console.log(`[DonationsByCustomerDialog] Order ${o.id}: ✅ included, amount=${amount}`);
          
          return {
            id: o.id,
            date: orderData.date_created_gmt || orderData.date_created || '',
            amount: amount,
            paymentMethod: orderData.payment_method_title || orderData.payment_method || 'N/A',
            items: relevantItems
          };
        } catch (e) {
          console.error(`[DonationsByCustomerDialog] Failed to load order ${o.id}:`, e);
          return null;
        }
      }));
      
      // Filter out null entries (orders that don't have the product/variation)
      const validEnriched = enriched.filter(tx => tx !== null);
      
      console.log(`[DonationsByCustomerDialog] Enriched ${validEnriched.length} transactions (${enriched.length - validEnriched.length} filtered out)`);
      
      setCustomerTransactions(prev => ({ ...prev, [customerKey]: validEnriched }));
    } catch (e) {
      console.error('[DonationsByCustomerDialog] Failed to load transactions:', e);
    } finally {
      setLoadingTransactions(prev => {
        const next = { ...prev };
        delete next[customerKey];
        return next;
      });
    }
  };

  // Calculate totals
  const totals = rows.reduce((acc, r) => ({
    count: acc.count + 1,
    total_amount: acc.total_amount + Number(r.total_amount || 0),
    order_count: acc.order_count + Number(r.order_count || 0)
  }), { count: 0, total_amount: 0, order_count: 0 });

  const variationName = product?.variation?.variation_name || product?.variation?.name || (product?.variation?.variation_id !== undefined ? `Variation #${product?.variation?.variation_id}` : '');
  const productName = product?.product_name || `Product #${product?.product_id}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h6">Donations by Customer</Typography>
          <Typography variant="body2" color="text.secondary">
            {productName} {variationName ? `- ${variationName}` : ''}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ minHeight: '400px' }}>
        {rows.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ p: 3, textAlign: 'center' }}>
            No donations found
          </Typography>
        ) : (
          <>
            {/* Totals Row */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px', gap: 1, p: 1, mb: 1, bgcolor: 'primary.light', borderRadius: 1, '& *': { color: 'primary.contrastText', fontWeight: 700 } }}>
              <Typography variant="body2">Total: {totals.count} customers</Typography>
              <Typography variant="body2" sx={{ textAlign: 'right' }}>{totals.order_count} orders</Typography>
              <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatAmount(totals.total_amount)}</Typography>
            </Box>

            {/* Table Header */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '32px 1fr 120px 140px', gap: 1, p: 1, bgcolor: 'action.hover', borderRadius: 1, mb: 1 }}>
              <Box></Box>
              <Typography variant="caption" sx={{ fontWeight: 700 }}>Customer</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>Orders</Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>Total</Typography>
            </Box>

            {/* Customer Rows */}
            <Box>
              {rows.map((customer, idx) => {
                const customerKey = customer.customer_key || `${customer.email || customer.customer_id || idx}`;
                const isExpanded = !!expandedCustomers[customerKey];
                const transactions = customerTransactions[customerKey] || [];
                const loading = !!loadingTransactions[customerKey];

                return (
                  <React.Fragment key={customerKey}>
                    <Box
                      onClick={() => toggleCustomerTransactions(customerKey, customer)}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '32px 1fr 120px 140px',
                        gap: 1,
                        p: 1,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        bgcolor: idx % 2 === 0 ? 'transparent' : 'action.hover',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: 'action.selected' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        {customer.customer_id ? (
                          <Link
                            component="button"
                            variant="body2"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/devotees/${encodeURIComponent(customer.customer_id)}`);
                            }}
                            sx={{
                              textDecoration: 'none',
                              color: 'primary.main',
                              '&:hover': {
                                textDecoration: 'underline',
                              },
                              cursor: 'pointer',
                            }}
                          >
                            {customer.name || 'Unknown'}
                          </Link>
                        ) : (
                          <Typography variant="body2">{customer.name || 'Unknown'}</Typography>
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ textAlign: 'right' }}>{Number(customer.order_count || 0).toLocaleString()}</Typography>
                      <Typography variant="body2" sx={{ textAlign: 'right', fontWeight: 600 }}>{formatAmount(customer.total_amount)}</Typography>
                    </Box>

                    {/* Transactions */}
                    {isExpanded && (
                      <Box sx={{ pl: 4, pr: 2, pb: 2 }}>
                        {loading ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>Loading transactions...</Typography>
                        ) : transactions.length === 0 ? (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No transactions found</Typography>
                        ) : (
                          <Box sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, overflow: 'hidden' }}>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 120px 140px 120px', gap: 1, p: 1, bgcolor: 'action.hover' }}>
                              <Typography variant="caption" sx={{ fontWeight: 700 }}>Date</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>Order ID</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>Amount</Typography>
                              <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'right' }}>Payment</Typography>
                            </Box>
                            {transactions.map((tx, txIdx) => (
                              <Box
                                key={tx.id}
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: '1fr 120px 140px 120px',
                                  gap: 1,
                                  p: 1,
                                  borderTop: '1px solid',
                                  borderColor: 'divider',
                                  bgcolor: txIdx % 2 === 0 ? 'transparent' : 'action.hover'
                                }}
                              >
                                <Typography variant="body2">{tx.date ? new Date(tx.date).toLocaleDateString() : 'N/A'}</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right' }}>#{tx.id}</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right' }}>{formatAmount(tx.amount)}</Typography>
                                <Typography variant="body2" sx={{ textAlign: 'right' }}>{tx.paymentMethod}</Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Box>
                    )}
                  </React.Fragment>
                );
              })}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}


